// Settings → Connect iPhone: the desktop half of QR pairing (mobile build plan §5, C1/C3/C4).
// The QR URL carries the one-time secret in its fragment; it is rendered, never logged.
import { z } from "zod";
import { connectionCaller, request } from "./device-sessions-api.js";

export type PairingStatus =
  | "pending"
  | "claimed"
  | "contested"
  | "approved"
  | "denied"
  | "expired"
  | "redeemed"
  | "failed"
  | "cancelled";

const STATUSES = new Set<string>([
  "pending",
  "claimed",
  "contested",
  "approved",
  "denied",
  "expired",
  "redeemed",
  "failed",
  "cancelled",
]);

const startSchema = z.object({
  pairingId: z.string().min(1).max(256),
  qrUrl: z.string().url().max(2048),
  expiresAt: z.string().datetime(),
});

// Everything about the phone is the phone's own claim (attacker-supplied, C2): shown labelled,
// capped, never trusted. `matchCode` is deliberately NOT read: the person types it from the phone.
const capped = (max: number) =>
  z
    .string()
    .transform((v) => [...v].slice(0, max).join(""))
    .nullable()
    .optional()
    .transform((v) => v ?? null); // null: an absent wire field reads as "not given"
const statusSchema = z.object({
  status: z
    .string()
    .transform((s): PairingStatus | "unknown" =>
      STATUSES.has(s) ? (s as PairingStatus) : "unknown",
    ),
  claimedDeviceName: capped(80),
  platform: capped(40),
  approxLocation: capped(120),
  asn: capped(120),
  locationMismatch: z.boolean().optional().default(false),
  /** This phone install already has a live sign-in here; approving replaces it. */
  alreadyPaired: z.boolean().optional().default(false),
  /** The account the phone says it is signed in to now, when it is not this one. */
  otherAccount: capped(200),
  expiresAt: z.string().datetime(),
});

export type PairingStart = z.infer<typeof startSchema>;
export type PairingState = z.infer<typeof statusSchema>;

export async function startPairing(base: string, token: string): Promise<PairingStart> {
  const parsed = startSchema.safeParse(await request(base, token, "/api/auth/pair/start", "POST"));
  if (!parsed.success) throw new Error("Invalid pairing response");
  return { ...parsed.data, qrUrl: pairingQrUrl(parsed.data.qrUrl, base) };
}

// The phone's defaults (AppModel.cloudBaseURL, the applinks: host): a QR for any other relay
// names it, so the phone claims there (after confirming the host) instead of on Cyborg Cloud.
const CLOUD_RELAY_ORIGIN = "https://relay.cyborg7.com";
const PAIR_LINK = "https://app.cyborg7.com/pair/";

/** `https://app.cyborg7.com/pair/#v1.<id>.<secret>[.<base64url(relayOrigin)>]` (plan §5). */
export function pairingQrUrl(serverQrUrl: string, relayBase: string): string {
  const fragment = serverQrUrl.slice(serverQrUrl.indexOf("#") + 1);
  const origin = new URL(relayBase).origin;
  // A relay that already names itself (4 segments) is left alone.
  if (fragment.split(".").length !== 3 || origin === CLOUD_RELAY_ORIGIN)
    return `${PAIR_LINK}#${fragment}`;
  const encoded = btoa(origin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  return `${PAIR_LINK}#${fragment}.${encoded}`;
}

export async function getPairing(base: string, token: string, id: string): Promise<PairingState> {
  const parsed = statusSchema.safeParse(
    await request(base, token, `/api/auth/pair/${encodeURIComponent(id)}`),
  );
  if (!parsed.success) throw new Error("Invalid pairing status response");
  return parsed.data;
}

export async function decidePairing(
  base: string,
  token: string,
  id: string,
  decision: "approve" | "deny",
  matchCode?: string,
): Promise<void> {
  await request(
    base,
    token,
    `/api/auth/pair/${encodeURIComponent(id)}/${decision}`,
    "POST",
    decision === "approve" ? { matchCode } : {},
  );
}

export interface PairingApi {
  start(): Promise<PairingStart>;
  get(id: string): Promise<PairingState>;
  approve(id: string, matchCode: string): Promise<void>;
  deny(id: string): Promise<void>;
}

interface PairingClient {
  startPairing(): Promise<PairingStart>;
  getPairing(id: string): Promise<PairingState>;
  decidePairing(id: string, decision: "approve" | "deny", matchCode?: string): Promise<void>;
  refreshAuthToken(): Promise<string>;
}

export function pairingForConnection(client: PairingClient, isCurrent: () => boolean): PairingApi {
  const perform = connectionCaller(client, isCurrent);
  return {
    start: () => perform(() => client.startPairing()),
    get: (id) => perform(() => client.getPairing(id)),
    approve: (id, code) => perform(() => client.decidePairing(id, "approve", code)),
    deny: (id) => perform(() => client.decidePairing(id, "deny")),
  };
}
